import { jsPDF } from 'jspdf'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { FormatOption } from '@/scenes/Editor/components/Navbar/components/downloadTypes'
import { customAmplitude } from '@/utils/customAmplitude'
import store from '@/store/store'
import { fetchVideoBlob, saveVideos } from '@/services/download/videoExporter'
import { noteLimitedOfferDownload } from '@/components/ShowModal/LimitedOffer/limitedOfferTrigger'

/**
 * Canvas-independent image export helpers.
 *
 * This is the canonical implementation for saving pre-rendered image data-URLs
 * (single, ZIP, or PDF) and for converting between formats. It is shared by:
 *  - `DownloadHandler.download()` (callback-image scope and scene scope)
 *  - The canvas-less Photoshoots dashboard route
 *
 * Do not duplicate this logic in components or other handlers.
 */

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpeg',
  'image/png': 'png',
  'image/webp': 'webp',
}

/**
 * Converts a base64 data URL to a Blob without going through fetch.
 * Canonical replacement for byte-for-byte duplicates that previously lived
 * in `DownloadHandler`, `ExportImage`, and `DownloadPopover`.
 */
export function base64ToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || ''
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

/**
 * Builds a jsPDF document from an array of data-URL images and returns it as
 * a Blob. Extracted from `saveImages` so that both the save-to-disk path and
 * the bundle-into-ZIP path can share the same PDF generation logic.
 */
async function buildPdfBlob(images: string[], baseFilename: string): Promise<Blob> {
  const scale = 72 / 128 // dpi conversion between canvas px and PDF pt

  const SUPPORTED_PDF_MIMES = new Set(['image/jpeg', 'image/png'])
  const normaliseToPdfDataUrl = async (
    src: string,
    img: HTMLImageElement,
  ): Promise<string> => {
    const mime = src.match(/^data:([^;,]+)/)?.[1] ?? ''
    if (SUPPORTED_PDF_MIMES.has(mime)) return src
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return src
    ctx.drawImage(img, 0, 0)
    return canvas.toDataURL('image/jpeg', 0.92)
  }

  const getJsPdfImageType = (dataUrl: string): 'PNG' | 'JPEG' => {
    const mime = dataUrl.match(/^data:([^;,]+)/)?.[1] ?? ''
    return mime === 'image/png' ? 'PNG' : 'JPEG'
  }

  const firstImg = await loadImage(images[0])
  const firstEmbedUrl = await normaliseToPdfDataUrl(images[0], firstImg)
  const firstW = firstImg.naturalWidth * scale
  const firstH = firstImg.naturalHeight * scale
  const pdf = new jsPDF({
    orientation: firstImg.naturalWidth > firstImg.naturalHeight ? 'landscape' : 'portrait',
    unit: 'px',
    format: [firstW, firstH],
  })

  for (const [index, dataUrl] of images.entries()) {
    const img = index === 0 ? firstImg : await loadImage(dataUrl)
    const embedUrl = index === 0 ? firstEmbedUrl : await normaliseToPdfDataUrl(dataUrl, img)
    const w = img.naturalWidth * scale
    const h = img.naturalHeight * scale
    if (index > 0) {
      pdf.addPage([w, h], img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait')
    }
    pdf.addImage(embedUrl, getJsPdfImageType(embedUrl), 0, 0, w, h)
  }

  return pdf.output('blob') as Blob
}

/**
 * Converts an array of data-URL images to the target format via offscreen canvas.
 * Images already in the target format are returned unchanged.
 * Transparent pixels are composited on white when converting to JPEG.
 */
export async function convertImagesToFormat(
  images: string[],
  format: string,
  quality: number,
): Promise<string[]> {
  const targetMime = `image/${format}`
  return Promise.all(
    images.map(
      dataUrl =>
        new Promise<string>(resolve => {
          const currentMime = dataUrl.match(/^data:([^;,]+)/)?.[1] ?? ''
          if (currentMime === targetMime) {
            resolve(dataUrl)
            return
          }
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) {
              resolve(dataUrl)
              return
            }
            if (format === 'jpeg') {
              // JPEG has no alpha channel; fill white to avoid black transparency artefacts.
              ctx.fillStyle = '#ffffff'
              ctx.fillRect(0, 0, canvas.width, canvas.height)
            }
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL(targetMime, quality))
          }
          img.onerror = () => resolve(dataUrl)
          img.src = dataUrl
        }),
    ),
  )
}

/**
 * Composes and saves an array of pre-rendered image data-URL strings.
 *
 * - PDF: infers each page's dimensions from the image's intrinsic pixel size,
 *   then composes a multi-page jsPDF document. Non-JPEG/PNG inputs are
 *   normalised to JPEG before being embedded.
 * - Single non-PDF: triggers a direct `<a>` download with the correct extension.
 * - Multiple non-PDF: packages all images into a ZIP archive named `bazaart.zip`.
 *
 * The optional `baseFilename` controls the saved filename for the single/zip
 * variants; defaults to `Bazaart` to match historical behavior.
 * `onSaved` runs synchronously after a guarded browser file handoff succeeds;
 * it does not establish that the browser finished writing a file to disk.
 */
export async function saveImages(
  images: string[],
  format: FormatOption,
  baseFilename = 'Bazaart',
  { canSave = () => true, onSaved }: { canSave?: () => boolean; onSaved?: () => void } = {},
): Promise<void> {
  if (images.length === 0 || !canSave()) return

  const firstMime = images[0]?.match(/^data:([^;,]+)/)?.[1] ?? ''
  const ext = MIME_TO_EXT[firstMime] ?? format.title.toLowerCase()

  if (format.title === 'pdf') {
    const blob = await buildPdfBlob(images, baseFilename)
    if (canSave()) {
      saveAs(blob, `${baseFilename}.pdf`)
      onSaved?.()
    }
    return
  }

  if (images.length === 1) {
    const a = document.createElement('a')
    a.href = images[0]
    a.download = `${baseFilename}.${ext}`
    a.click()
    onSaved?.()
    return
  }

  const zip = new JSZip()
  for (const [index, image] of images.entries()) {
    zip.file(`${baseFilename}-${index + 1}.${ext}`, base64ToBlob(image))
  }
  const blob = await zip.generateAsync({ type: 'blob' })
  if (canSave()) {
    saveAs(blob, `${baseFilename.toLowerCase()}.zip`)
    onSaved?.()
  }
}

/**
 * Blob-native counterpart to `saveImages`. Accepts pre-rendered Blobs so callers
 * that already have Blobs (e.g. from canvas.toBlob) avoid the base64 round-trip
 * that `saveImages` would require. All blobs are assumed to share the same format
 * extension (e.g. 'jpeg', 'png', 'webp').
 *
 * - Single blob: direct `saveAs` download.
 * - Multiple blobs: packaged into `{baseFilename}.zip`.
 */
export async function saveBlobImages(
  blobs: Blob[],
  ext: string,
  baseFilename = 'Bazaart',
  filenames?: string[],
): Promise<void> {
  if (blobs.length === 0) return

  if (blobs.length === 1) {
    saveAs(blobs[0], filenames?.[0] ?? `${baseFilename}.${ext}`)
    return
  }

  const zip = new JSZip()
  for (const [index, blob] of blobs.entries()) {
    zip.file(filenames?.[index] ?? `${baseFilename}-${index + 1}.${ext}`, blob)
  }
  saveAs(await zip.generateAsync({ type: 'blob' }), `${baseFilename.toLowerCase()}.zip`)
}

/**
 * Convenience entry point for downloading callback-provided images
 * (i.e. images that already exist as data URLs and do not need to be rendered
 * from a Fabric scene). Handles format conversion, then delegates to
 * `saveImages` for save/compose.
 *
 * Used by the canvas-less Photoshoots route via `DownloadPopover` and the
 * page-level Download all control.
 */
export async function exportImages(
  images: string[],
  format: FormatOption,
  quality: number,
  baseFilename = 'Bazaart',
): Promise<void> {
  if (images.length === 0) return
  const prepared =
    format.title === 'pdf' ? images : await convertImagesToFormat(images, format.title, quality)
  await saveImages(prepared, format, baseFilename)
}

/**
 * Canonical analytics reporter for all image download events ("Image Shared").
 *
 * Covers both editor-coupled downloads (via `DownloadHandler`) and canvas-less
 * downloads (e.g. the Photoshoots dashboard route). All params beyond
 * `formatTitle` are optional — pass only those available in the current context.
 *
 * @param params.numberOfPages            - Page / scene count (scenes path) or image count (callback path)
 * @param params.numberOfLayers           - Layer count; only available when the editor is mounted
 * @param params.layerTypeStock           - Whether any layer is a stock image
 * @param params.layerTypeMagicBackground - Whether any layer is a magic-background layer
 * @param params.layerTypeMagicDesign     - Whether any layer is a magic-design layer
 * @param params.finger                   - 1 if any layer has finger data, 0 otherwise
 * @param params.routeId                  - URL route ID used as a template-ID fallback
 */
export function reportImageDownload(params: {
  allowMarketingOffers?: boolean
  formatTitle: string
  qualityOption?: string
  routeId?: string
  source?: string
  destination?: string
  videoLength?: number | string
  numberOfPages?: number
  numberOfLayers?: number
  layerTypeStock?: boolean
  layerTypeMagicBackground?: boolean
  layerTypeMagicDesign?: boolean
  finger?: number
}): void {
  const {
    formatTitle,
    qualityOption,
    routeId,
    source = 'Editor',
    destination = 'Desktop',
    videoLength,
    numberOfPages,
    numberOfLayers,
    layerTypeStock,
    layerTypeMagicBackground,
    layerTypeMagicDesign,
    finger,
  } = params
  const state = store.getState()
  const templateConfig = state.editor.templates.templateConfig
  const backgroundActive = state.editor.templates.backgroundActive

  const parsedRouteId = routeId != null ? Number(routeId) : NaN
  const template_id = templateConfig?.templateId
    ? templateConfig.templateId
    : !Number.isNaN(parsedRouteId) && parsedRouteId !== 6764
    ? parsedRouteId
    : undefined

  const eventProperties: Record<string, any> = {
    Watermark: !state.editor.user.userIsPremium,
    Template: !!template_id,
    'Template ID': templateConfig?.templateId ?? template_id,
    'Template Category': templateConfig?.templateCategory ?? 'none',
    Format: formatTitle,
    Destination: destination,
    Source: source,
  }

  if (numberOfLayers !== undefined) eventProperties['Number of Layers'] = numberOfLayers
  if (numberOfPages !== undefined) eventProperties['Number of Pages'] = numberOfPages
  if (qualityOption !== undefined) eventProperties['Quality'] = qualityOption
  if (layerTypeStock !== undefined) eventProperties['layer_type_stock'] = layerTypeStock
  if (layerTypeMagicBackground !== undefined) eventProperties['layer_type_magic_background'] = layerTypeMagicBackground
  if (layerTypeMagicDesign !== undefined) eventProperties['layer_type_magic_design'] = layerTypeMagicDesign
  if (finger !== undefined) eventProperties['finger'] = finger
  if (videoLength !== undefined) eventProperties['video length'] = videoLength

  if (backgroundActive) {
    eventProperties['Background ID'] = backgroundActive.id
    eventProperties['Background Category'] = backgroundActive.category
  }

  customAmplitude('Image Shared', eventProperties)
  // @ts-ignore
  window.dataLayer.push({ event: 'download_click', ...eventProperties })
  // Most export paths report here; the few that emit Image Shared directly also call this.
  noteLimitedOfferDownload({ showOffer: params.allowMarketingOffers !== false })
}

export type CombinedBundleImage = {
  /** Data URL in the target format (for non-PDF), or raw data URL for PDF. */
  dataUrl: string
  /** File extension: 'jpeg', 'png', 'webp', or 'pdf'. */
  ext: string
}

/**
 * Downloads images and videos as a single ZIP archive when both types are
 * present, so the user receives one download prompt instead of two.
 *
 * - No videos  → no-op; caller is responsible for image-only saves.
 * - No images  → saves videos only via saveVideos.
 * - Both types → combines everything into one ZIP.
 *   PDF format: generates a single PDF blob from all image pages and adds it
 *   to the ZIP alongside the video(s), so even PDF + video is a single prompt.
 */
export async function saveCombinedBundle(params: {
  images: CombinedBundleImage[]
  videos: Array<{ url: string }>
  baseFilename?: string
  zipFilename?: string
}): Promise<void> {
  const {
    images,
    videos,
    baseFilename = 'Bazaart',
    zipFilename = 'bazaart.zip',
  } = params

  if (videos.length === 0) return // caller handles image-only saves

  if (images.length === 0) {
    await saveVideos(videos.map(v => ({ url: v.url })), { baseFilename, zipFilename })
    return
  }

  // At least one image and one video → combine into a single ZIP.
  const zip = new JSZip()

  if (images[0].ext === 'pdf') {
    // All images form one multi-page PDF.
    const pdfBlob = await buildPdfBlob(images.map(i => i.dataUrl), baseFilename)
    zip.file(`${baseFilename}.pdf`, pdfBlob)
  } else {
    for (const [i, { dataUrl, ext }] of images.entries()) {
      const name = images.length === 1
        ? `${baseFilename}.${ext}`
        : `${baseFilename}-${i + 1}.${ext}`
      zip.file(name, base64ToBlob(dataUrl))
    }
  }

  const videoResults = await Promise.allSettled(
    videos.map(async (v, i) => {
      const name = videos.length === 1
        ? `${baseFilename}.mp4`
        : `${baseFilename}-${i + 1}.mp4`
      return { name, blob: await fetchVideoBlob(v.url) }
    }),
  )

  for (const result of videoResults) {
    if (result.status === 'fulfilled') {
      zip.file(result.value.name, result.value.blob)
    } else {
      console.error('saveCombinedBundle: video fetch failed, skipping:', result.reason)
    }
  }

  saveAs(await zip.generateAsync({ type: 'blob' }), zipFilename)
}
